import {
  Box,
  Button,
  Chip,
  Group,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  DataTable,
  EmptyState,
  ErrorState,
  ExportMenu,
  LoadingSkeleton,
  RecordCard,
  StateBadge,
  SwipeableCard,
  useColumns,
  useSlashToFocus,
} from "../../components/ui";
import { serviceJobState } from "../../components/ui/statusBadge";
import { STATE } from "../../theme/mantine-theme";
import { api, type ServiceJob } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * One screen for four lists, chosen by the path:
 *
 *   /service/queue     everything still moving through service
 *   /service/delivery  finished here, ready for delivery back to the customer
 *   /service/mine      a collection agent's own pickups
 *   /service/jobs      everything, finished included
 *
 * The next action is shown per row, because in a workshop the question is
 * always "what does this one need now" rather than "what is its status".
 *
 * WHAT THE DESIGN ASKED FOR AND WHAT IS HERE
 *
 * The stage strip, the filter chips and the grouping are the design's. Three
 * things in it are not, and each is left out rather than faked:
 *
 *  - an "Overdue" filter and a "4d in stage" chip that turns orange over SLA.
 *    Days in stage is real and shown. There is no SLA anywhere in this system
 *    to be over, so nothing here judges a job late - inventing a threshold
 *    would put a number in front of the workshop that the business never set.
 *  - a "Warranty" filter. The business removed the warranty logic; the
 *    Service Manager settles it with the customer.
 *  - swipe-to-advance on every row. Only one transition in this lifecycle is
 *    a single confirmation with nothing to fill in - COLLECTED to RECEIVED -
 *    so that is the only row that swipes. Everything else needs an outcome,
 *    an amount or a role check, and a swipe that opens a form is a tap with
 *    extra steps.
 */

/** The stages a job passes through, for the counter strip and the grouping. */
const STAGES = [
  { key: "COLLECTED", label: "Collected", token: "svcCollected" },
  { key: "RECEIVED", label: "At the workshop", token: "svcReceived" },
  { key: "DIAGNOSED", label: "Diagnosed", token: "svcDiagnosed" },
  { key: "OUTCOME", label: "Outcome", token: "svcServiced" },
  { key: "DELIVERED", label: "Delivered back", token: "svcDelivered" },
] as const;

/** Which strip tile a job belongs under. The three outcomes share one. */
function stageOf(job: ServiceJob): (typeof STAGES)[number]["key"] {
  switch (job.status) {
    case "COLLECTED":
      return "COLLECTED";
    case "RECEIVED":
      return "RECEIVED";
    case "DIAGNOSED":
      return "DIAGNOSED";
    case "SERVICED":
    case "REPLACED":
    case "REJECTED":
      return "OUTCOME";
    default:
      return "DELIVERED";
  }
}

/** What this job is waiting for, in the words of the person who does it. */
function nextStep(job: ServiceJob): string | undefined {
  switch (job.status) {
    case "COLLECTED":
      return "Confirm it has arrived";
    case "RECEIVED":
      return "Record the diagnosis";
    case "DIAGNOSED":
      // Only the two outcomes that ISSUE a battery raise an order; OK and
      // REJECTED just need carrying out, and saying "raise the order" for
      // those would describe a step that does not exist for them.
      return job.outcome === "FOC" || job.outcome === "PRORATA"
        ? "Place the replacement order"
        : job.outcome === "OK"
          ? "Confirm it is OK and ready to go back"
          : "Confirm it is ready to go back";
    case "SERVICED":
    case "REJECTED":
      return "Deliver it back to the customer";
    case "REPLACED":
      return job.order?.bill ? "Deliver it to the customer" : "Bill it, then deliver";
    default:
      return undefined;
  }
}

/**
 * Whole days since the job last moved.
 *
 * Reported, never judged. See the note at the top: there is no SLA in this
 * system, so "4 days in stage" is a fact and "4 days is too long" would be an
 * opinion nobody has given us.
 */
function daysInStage(job: ServiceJob): number {
  const since = job.diagnosedAt ?? job.receivedAt ?? job.collectedAt;
  const ms = Date.now() - new Date(since).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function ageLabel(job: ServiceJob): string {
  const days = daysInStage(job);
  if (days === 0) return "today";
  return days === 1 ? "1 day in stage" : `${days} days in stage`;
}

export function ServiceQueue() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const scope = pathname.endsWith("/mine")
    ? "mine"
    : pathname.endsWith("/queue")
      ? "queue"
      : pathname.endsWith("/delivery")
        ? "delivery"
        : undefined;

  const [jobs, setJobs] = useState<ServiceJob[]>();
  const [error, setError] = useState<string>();
  const [stage, setStage] = useState<string>();
  const [mineOnly, setMineOnly] = useState(false);
  const [receiving, setReceiving] = useState<string>();
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashToFocus(searchRef);

  // Receiving a battery into the workshop is the workshop's job. Offering the
  // swipe to anyone else would be offering an action the API refuses.
  const canReceive = hasRole(
    "INDOOR_SERVICE_TECHNICIAN",
    "SERVICE_MANAGER",
    "STORE_MANAGER",
    "ADMIN",
  );

  async function load() {
    try {
      setError(undefined);
      const { jobs: loaded } = await api.serviceJobs(scope);
      setJobs(loaded);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load jobs.");
    }
  }

  useEffect(() => {
    setJobs(undefined);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const heading =
    scope === "mine"
      ? "My collections"
      : scope === "queue"
        ? "Service Center List"
        : scope === "delivery"
          ? "Ready for Delivery"
          : "Service Summary";

  const counts = useMemo(() => {
    const tally: Record<string, number> = {};
    for (const job of jobs ?? []) {
      const key = stageOf(job);
      tally[key] = (tally[key] ?? 0) + 1;
    }
    return tally;
  }, [jobs]);

  const visible = useMemo(() => {
    let rows = jobs ?? [];
    if (stage) rows = rows.filter((job) => stageOf(job) === stage);
    if (mineOnly && user) {
      rows = rows.filter(
        (job) =>
          job.collectionAgent?.id === user.id || job.technician?.id === user.id,
      );
    }
    // Searches what is on the row: the job number, the customer, the serial
    // off the battery and the model. Client-side because the list is already
    // here - a round trip to filter rows the browser is holding would make
    // typing feel slower than reading.
    const needle = query.trim().toLowerCase();
    if (needle) {
      rows = rows.filter(
        (job) =>
          job.reference.toLowerCase().includes(needle) ||
          job.customer.name.toLowerCase().includes(needle) ||
          (job.serialNumber ?? "").toLowerCase().includes(needle) ||
          (job.collectedProduct?.name ?? "").toLowerCase().includes(needle),
      );
    }
    return rows;
  }, [jobs, stage, mineOnly, user, query]);

  /** Rows grouped under their stage, in lifecycle order. */
  const groups = useMemo(
    () =>
      STAGES.map((entry) => ({
        ...entry,
        rows: visible.filter((job) => stageOf(job) === entry.key),
      })).filter((group) => group.rows.length > 0),
    [visible],
  );

  /** Who is holding the battery right now. */
  function whoHasIt(job: ServiceJob): string {
    if (job.status === "COLLECTED") return job.collectionAgent?.name ?? "—";
    return job.technician?.name ?? job.serviceManager?.name ?? "The workshop";
  }

  /**
   * Is the next step THIS side of the wall?
   *
   * A job that is finished in Service is waiting on Delivery, and rendering
   * "Deliver it back to the customer" as an action link would be offering a
   * step this screen cannot take. It reads as plain text instead, naming who
   * it is waiting on.
   */
  function waitingOnDelivery(job: ServiceJob): boolean {
    return job.status === "SERVICED" || job.status === "REJECTED" || job.status === "REPLACED";
  }

  const columns = useColumns<ServiceJob>(
    () => [
      {
        key: "Job",
        header: "Job",
        width: "140px",
        render: (job) => (
          <Text fz={13} fw={600} className="num" truncate>
            {job.reference}
          </Text>
        ),
      },
      {
        key: "Customer",
        header: "Customer",
        width: "auto",
        render: (job) => (
          <Text fz={14} truncate>
            {job.customer.name}
          </Text>
        ),
      },
      {
        key: "Model",
        header: "Model",
        width: "160px",
        // The capacity the brief shows as "Ah" is part of the model's name
        // in the real catalogue - "Amaron Flo 40Ah" - so it is here already
        // and there is no capacity column to add.
        render: (job) => (
          <Text fz={14} truncate c={job.collectedProduct ? undefined : "gray.6"}>
            {job.collectedProduct?.name ?? "—"}
          </Text>
        ),
      },
      {
        key: "Serial no.",
        header: "Serial no.",
        width: "150px",
        render: (job) =>
          job.serialNumber ? (
            <Text fz={13} className="num" truncate>
              {job.serialNumber}
            </Text>
          ) : (
            <Text fz={13} c="gray.6">
              —
            </Text>
          ),
      },
      {
        key: "Stage",
        header: "Stage",
        width: "130px",
        render: (job) => (
          <StateBadge state={serviceJobState(job.status) ?? "svcCollected"} />
        ),
      },
      {
        key: "In stage",
        header: "In stage",
        width: "90px",
        align: "right",
        // Reported, never judged. No colour, no threshold - there is no SLA
        // in this system, so nothing here may call a row late.
        render: (job) => (
          <Text fz={13} className="num" c="gray.7">
            {daysInStage(job) === 0 ? "today" : `${daysInStage(job)}d`}
          </Text>
        ),
      },
      {
        key: "Who has it",
        header: "Who has it",
        width: "140px",
        render: (job) => (
          <Text fz={14} truncate c="gray.7">
            {whoHasIt(job)}
          </Text>
        ),
        value: whoHasIt,
      },
      {
        key: "Next step",
        header: "Next step",
        width: "190px",
        render: (job) =>
          waitingOnDelivery(job) ? (
            <Text fz={14} c="gray.6" truncate>
              Waiting on Delivery
            </Text>
          ) : (
            <Text fz={14} fw={600} c="indigo.7" truncate>
              {nextStep(job) ?? "—"}
            </Text>
          ),
      },
    ],
    [],
  );

  async function receive(job: ServiceJob) {
    setReceiving(job.id);
    try {
      await api.receiveServiceJob(job.id);
      notifications.show({
        color: "teal",
        message: `${job.reference} is at the workshop.`,
      });
      await load();
    } catch (caught) {
      notifications.show({
        color: "red",
        autoClose: false,
        title: "Could not receive it",
        message: caught instanceof Error ? caught.message : "Try again.",
      });
    } finally {
      setReceiving(undefined);
    }
  }

  return (
    <Page
      title={heading}
      back="/service"
      width="wide"
      subtitle={
        jobs
          ? scope === "queue"
            ? `${jobs.length} still in service`
            : scope === "delivery"
              ? `${jobs.length} ready for a delivery agent`
              : `${jobs.length} job${jobs.length === 1 ? "" : "s"}`
          : undefined
      }
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {!jobs && !error && <LoadingSkeleton variant="card" count={4} />}

      {jobs && jobs.length > 0 && (
        <>
          {/* The stage strip. Each tile carries a top rule in its own state
              colour, and the count is the point of it - a number plus a word,
              never a colour on its own. */}
          <SimpleGrid cols={{ base: 5 }} spacing={6}>
            {STAGES.map((entry) => {
              const token = STATE[entry.token];
              const active = stage === entry.key;
              return (
                <UnstyledButton
                  key={entry.key}
                  onClick={() => setStage(active ? undefined : entry.key)}
                  aria-pressed={active}
                  style={{
                    borderRadius: "var(--mantine-radius-md)",
                    border: `1px solid var(--mantine-color-gray-${active ? "5" : "3"})`,
                    background: active ? token.bg : "var(--mantine-color-white)",
                    overflow: "hidden",
                    minHeight: 60,
                  }}
                >
                  <Box style={{ height: 3, background: token.fg }} />
                  <Stack gap={0} p={6} align="center">
                    <Text fz="lg" fw={700} className="num" c="gray.9" lh={1.2}>
                      {counts[entry.key] ?? 0}
                    </Text>
                    <Text fz={10} c="gray.6" ta="center" lh={1.15}>
                      {entry.label}
                    </Text>
                  </Stack>
                </UnstyledButton>
              );
            })}
          </SimpleGrid>

          <ScrollArea type="never">
            <Group gap="xs" wrap="nowrap">
              <Chip
                checked={!stage && !mineOnly}
                onClick={() => {
                  setStage(undefined);
                  setMineOnly(false);
                }}
                variant="filled"
              >
                All stages
              </Chip>
              <Chip
                checked={mineOnly}
                onClick={() => setMineOnly((on) => !on)}
                variant={mineOnly ? "filled" : "outline"}
              >
                My jobs
              </Chip>
              {stage && (
                <Chip checked variant="filled" onClick={() => setStage(undefined)}>
                  {STAGES.find((entry) => entry.key === stage)?.label} ✕
                </Chip>
              )}
            </Group>
          </ScrollArea>
        </>
      )}

      {jobs?.length === 0 && (
        <EmptyState
          title={
            scope === "queue"
              ? "Nothing waiting"
              : scope === "delivery"
                ? "Nothing ready for delivery"
                : scope === "mine"
                  ? "No collections yet"
                  : "No service jobs yet"
          }
          description={
            scope === "queue"
              ? "Every battery has been dealt with."
              : scope === "mine"
                ? "Batteries you pick up will appear here."
                : undefined
          }
          action={
            hasRole("OUTDOOR_SERVICE_TECHNICIAN") ? (
              <Button onClick={() => navigate("/service/collect")}>
                Collect a battery
              </Button>
            ) : undefined
          }
        />
      )}

      {/* Phone only: the desktop table says this in its own empty row, and
          rendering both would put the message on screen twice. */}
      {jobs && jobs.length > 0 && visible.length === 0 && (
        <Box hiddenFrom="md">
        <EmptyState
          title="Nothing matches those filters"
          description="Clear them to see the whole list again."
          action={
            <Button
              variant="default"
              onClick={() => {
                setStage(undefined);
                setMineOnly(false);
              }}
            >
              Clear filters
            </Button>
          }
        />
        </Box>
      )}

      {/* Desktop: the brief's table. The columns are the ones you scan on -
          Stage and Next step - beside the identifiers you search on. */}
      {jobs && jobs.length > 0 && (
        <Box visibleFrom="md">
          <Stack gap={12}>
            <Group gap="sm" wrap="nowrap" align="flex-end">
              <TextInput
                ref={searchRef}
                id="svc-search"
                type="search"
                aria-label="Search the queue"
                placeholder="Job no., customer, serial no. or model"
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                w={320}
                rightSection={
                  <Text fz={11} c="gray.6" className="num" pr={4}>
                    /
                  </Text>
                }
              />
              <Box style={{ flex: 1 }} />
              {/* No count in the label any more. It used to say "Export 47
                  rows" because the file WAS those 47 - the filters, the stage
                  chips and the search included - and "Export" alone would
                  have been read as "export everything".

                  It now is everything: the server builds the file from the
                  whole workshop, past the 200-row cap this screen is served
                  under, and prints the real count inside it. */}
              <ExportMenu dataset="service-jobs" />
            </Group>

            <DataTable
              rows={visible}
              columns={columns}
              rowKey={(job) => job.id}
              onOpen={(job) => navigate(`/service/jobs/${job.id}`)}
              caption={`${heading}: ${visible.length} jobs`}
              emptyLabel="Nothing matches those filters."
            />

            <Group justify="space-between" gap="sm">
              <Text fz={12} c="gray.6">
                j / k or the arrow keys move, Enter opens, / searches. Days in
                stage is reported, never judged — there is no SLA in this
                system, so no row is marked late.
              </Text>
              {/* The server caps this SCREEN at 200. Saying so only when it
                  is actually at the cap keeps the warning meaningful.

                  It used to end "an export will not be the whole list", which
                  was the truth about the old export and is now the opposite
                  of it - the export reads past this cap. A warning that is
                  wrong in the reassuring direction is worse than none, so it
                  now points at the thing that still works. */}
              {jobs.length >= 200 && (
                <Text fz={12} c="yellow.8" fw={600}>
                  Showing the first 200 jobs. Export for the whole workshop.
                </Text>
              )}
            </Group>
          </Stack>
        </Box>
      )}

      {/* Phone: the grouped cards. A table at 390px is a table nobody reads. */}
      <Box hiddenFrom="md">
      <Stack gap="lg">
      {groups.map((group) => (
        <Stack key={group.key} gap="sm">
          <Group gap="xs" wrap="nowrap">
            <Box
              aria-hidden
              w={8}
              h={8}
              style={{
                borderRadius: 999,
                background: STATE[group.token].fg,
                flexShrink: 0,
              }}
            />
            <Text fz="xs" fw={700} c="gray.6" tt="uppercase" lts="0.04em">
              {group.label} · {group.rows.length}
            </Text>
          </Group>

          {group.rows.map((job) => {
            const row = (
              <RecordCard
                to={`/service/jobs/${job.id}`}
                primary={job.reference}
                secondary={job.customer.name}
                badges={
                  <>
                    <StateBadge state={serviceJobState(job.status) ?? "svcCollected"} />
                    {job.order && (
                      <StateBadge
                        state={job.order.bill ? "ordBilled" : "ordReadyToBill"}
                        label={job.order.bill ? "Billed" : "To bill"}
                      />
                    )}
                  </>
                }
                meta={
                  <>
                    {job.serialNumber && (
                      <span className="num">{job.serialNumber}</span>
                    )}
                    {job.collectedProduct && ` · ${job.collectedProduct.sku}`}
                    {job.batteryDetails && ` · ${job.batteryDetails}`}
                    {` · ${ageLabel(job)}`}
                    {nextStep(job) ? ` · next: ${nextStep(job)}` : ""}
                  </>
                }
              />
            );

            // The one transition that is a confirmation and nothing else.
            return job.status === "COLLECTED" && canReceive ? (
              <SwipeableCard
                key={job.id}
                leftLabel={receiving === job.id ? "Receiving…" : "At the workshop"}
                onSwipeLeft={() => void receive(job)}
              >
                <Stack gap="xs">
                  {row}
                  {/* The brief's rule, and the right one: a swipe is never the
                      only way to do something. This is the same action for
                      anybody not swiping, keyboard included. */}
                  <Button
                    variant="light"
                    color="teal"
                    size="sm"
                    loading={receiving === job.id}
                    onClick={() => void receive(job)}
                  >
                    Confirm it has arrived
                  </Button>
                </Stack>
              </SwipeableCard>
            ) : (
              <Box key={job.id}>{row}</Box>
            );
          })}
        </Stack>
      ))}
      </Stack>
      </Box>
    </Page>
  );
}
